import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from '@mui/material';
import { IconDownload, IconSearch } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type {
  CustomerIndexItemDto,
  InvoiceLineItemSearchResponse,
  InvoiceLineItemSearchRow,
  LineItemMatchField,
  LineItemMatchMode,
} from '@techgitter/contracts';
import {
  SecondaryToolbar,
  ToolbarButton,
  ToolbarCount,
  ToolbarDivider,
  ToolbarSpacer,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import { Chip } from '../components/chip';
import { DateField } from '../components/dropdown';
import { FormField } from '../components/panel/FormField';
import { RadioOption } from '../components/panel/RadioOption';
import { CustomerLookupControl } from '../components/tickets/Customer/CustomerLookupControl';
import { InvoicesViewSwitch } from '../components/invoices/InvoicesViewSwitch';
import { searchInvoiceLineItems } from '../services/invoiceLineItemsApi';
import {
  KEYWORD_PRESETS,
  activePresetKey,
  computeTotals,
  csvFilename,
  presetKeywords,
  searchBlockedReason,
  toCsv,
} from '../utils/lineItemExplorer';
import type { LineItemExplorerFilters } from '../utils/lineItemExplorer';
import { sortRows } from '../utils/lineItemExplorerSort';
import type { LineItemSortKey } from '../utils/lineItemExplorerSort';
import './LineItemExplorerPage.css';

const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const formatCurrency = (n: number): string => currency.format(Number(n) || 0);
const formatQty = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(2));
const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const MATCH_IN_OPTIONS: { value: LineItemMatchField; label: string }[] = [
  { value: 'both', label: 'Code + description' },
  { value: 'code', label: 'Product code' },
  { value: 'description', label: 'Description' },
];

const MODE_OPTIONS: { value: LineItemMatchMode; label: string }[] = [
  { value: 'any', label: 'Any term' },
  { value: 'all', label: 'All terms' },
];

const COLUMN_COUNT = 9;

/**
 * Line Item Explorer — invoice LINES across every invoice, filtered by
 * customer, invoice date and keywords (`/invoices/line-items`). Read-only.
 * Built for billing audits ("every domain line for X since Jan 2025").
 * Search is explicit (button / Enter) because a broad query takes seconds.
 */
export const LineItemExplorerPage: React.FC = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [keywords, setKeywords] = useState('');
  const [matchIn, setMatchIn] = useState<LineItemMatchField>('both');
  const [mode, setMode] = useState<LineItemMatchMode>('any');

  const [result, setResult] = useState<InvoiceLineItemSearchResponse | null>(null);
  const [searchedWith, setSearchedWith] = useState<LineItemExplorerFilters | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sort, setSort] = useState<{ key: LineItemSortKey; dir: 'asc' | 'desc' }>({
    key: 'date',
    dir: 'desc',
  });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const filters: LineItemExplorerFilters = useMemo(
    () => ({
      customerId: customer?.dataverseCustomerId ?? null,
      from,
      to,
      keywords,
      matchIn,
      mode,
    }),
    [customer, from, to, keywords, matchIn, mode],
  );
  const blocked = searchBlockedReason(filters);

  const runSearch = useCallback(async (f: LineItemExplorerFilters) => {
    if (searchBlockedReason(f)) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await searchInvoiceLineItems(f, controller.signal);
      setResult(data);
      setSearchedWith(f);
      setPage(0);
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setError((e as Error).message || 'Search failed');
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, []);

  const applyPreset = (terms: string) => {
    setKeywords(terms);
    setMode('any');
    runSearch({ ...filters, keywords: terms, mode: 'any' });
  };

  const rows = useMemo(
    () => (result ? sortRows(result.items, sort.key, sort.dir) : []),
    [result, sort],
  );
  const totals = useMemo(() => computeTotals(rows), [rows]);
  const pageRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const presetKey = activePresetKey(keywords);

  const handleSort = (key: LineItemSortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const exportCsv = () => {
    if (!rows.length || !searchedWith) return;
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvFilename(searchedWith, new Date().toLocaleDateString('en-CA'));
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const head = (key: LineItemSortKey, label: string, align?: 'right') => (
    <DataTableHeadCell
      align={align}
      sortable
      active={sort.key === key}
      direction={sort.dir}
      onSort={() => handleSort(key)}
    >
      {label}
    </DataTableHeadCell>
  );

  const renderRow = (r: InvoiceLineItemSearchRow) => (
    <DataTableRow
      key={`${r.invoiceId}:${r.lineItemId ?? r.position}`}
      onClick={() => navigate(`/invoice/${r.invoiceNumber}`)}
    >
      <DataTableCell bold>
        <span className="lie-invoice">
          {r.invoiceNumber}
          {r.rsInvoiceId && (
            <a
              className="lie-rs-link"
              href={`https://techsolvers.repairshopr.com/invoices/${r.rsInvoiceId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Open in RepairShopr"
            >
              RS
            </a>
          )}
        </span>
      </DataTableCell>
      <DataTableCell muted>{formatDate(r.invoiceDate)}</DataTableCell>
      <DataTableCell>{r.customerName || '—'}</DataTableCell>
      <DataTableCell>
        <span className="lie-code">{r.productCode || '—'}</span>
      </DataTableCell>
      <DataTableCell>
        <span className="lie-desc" title={r.description}>{r.description || '—'}</span>
      </DataTableCell>
      <DataTableCell align="right">{formatQty(r.quantity)}</DataTableCell>
      <DataTableCell align="right" muted>{formatCurrency(r.unitPrice)}</DataTableCell>
      <DataTableCell align="right" bold>{formatCurrency(r.lineTotal)}</DataTableCell>
      <DataTableCell>
        {r.isPaid ? <Chip tone="success">Paid</Chip> : <Chip tone="warning">Unpaid</Chip>}
      </DataTableCell>
    </DataTableRow>
  );

  return (
    <div className="lie-page">
      <SecondaryToolbar floating>
        <InvoicesViewSwitch active="line-items" />
        <ToolbarSpacer />
        <ToolbarCount>
          {loading
            ? 'Searching…'
            : result
              ? `${totals.lines} line${totals.lines === 1 ? '' : 's'} · ${totals.invoices} invoice${totals.invoices === 1 ? '' : 's'}`
              : 'No search yet'}
        </ToolbarCount>
        <ToolbarDivider />
        <ToolbarButton icon={IconDownload} onClick={exportCsv} disabled={!rows.length}>
          Export CSV
        </ToolbarButton>
      </SecondaryToolbar>

      <div className="lie-body">
        {/* Not a <form>: Enter also picks a customer and commits a typed date,
            and a form would submit a search with the state from before that. */}
        <div className="lie-card lie-filters">
          <div className="lie-filters__row">
            <div className="lie-filters__customer">
              <CustomerLookupControl value={customer} onChange={setCustomer} />
            </div>
            <div className="lie-filters__date">
              <DateField label="From" value={from || null} onChange={(v) => setFrom(v ?? '')} />
            </div>
            <div className="lie-filters__date">
              <DateField label="To" value={to || null} onChange={(v) => setTo(v ?? '')} />
            </div>
            <div className="lie-filters__keywords">
              <FormField label="Keywords (comma-separated, * = starts with)">
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runSearch(filters);
                    }
                  }}
                  placeholder="e.g. loadingagent, Hosting.Domain*"
                  aria-label="Keywords"
                />
              </FormField>
            </div>
            <button
              type="button"
              className="lie-search-btn"
              onClick={() => runSearch(filters)}
              disabled={!!blocked || loading}
              title={blocked ?? undefined}
            >
              <IconSearch size={16} stroke={1.5} />
              Search
            </button>
          </div>

          <div className="lie-filters__row lie-filters__row--options">
            <span className="lie-filters__label">Match in</span>
            <div className="radio-option-group radio-option-group--row">
              {MATCH_IN_OPTIONS.map((o) => (
                <RadioOption
                  key={o.value}
                  name="lie-match-in"
                  value={o.value}
                  checked={matchIn === o.value}
                  onChange={(v) => setMatchIn(v as LineItemMatchField)}
                  label={o.label}
                />
              ))}
            </div>
            <span className="lie-filters__label">Terms</span>
            <div className="radio-option-group radio-option-group--row">
              {MODE_OPTIONS.map((o) => (
                <RadioOption
                  key={o.value}
                  name="lie-mode"
                  value={o.value}
                  checked={mode === o.value}
                  onChange={(v) => setMode(v as LineItemMatchMode)}
                  label={o.label}
                />
              ))}
            </div>
            <span className="lie-filters__label">Presets</span>
            <div className="lie-presets">
              {KEYWORD_PRESETS.map((p) => (
                <Chip
                  key={p.key}
                  tone={presetKey === p.key ? 'info' : 'neutral'}
                  onClick={() => applyPreset(presetKeywords(p))}
                  title={p.terms.join(', ')}
                >
                  {p.label}
                </Chip>
              ))}
            </div>
          </div>
          {blocked && !customer && (
            <div className="lie-filters__hint">{blocked}</div>
          )}
        </div>

        {error && <Alert severity="error">{error}</Alert>}
        {result?.truncated && (
          <Alert severity="warning">
            More than {result.limit.toLocaleString()} lines matched — showing the newest{' '}
            {result.limit.toLocaleString()}, and the totals cover only those. Narrow the dates or
            keywords.
          </Alert>
        )}

        {result && rows.length > 0 && (
          <div className="lie-card lie-totals">
            <div className="lie-totals__fact">
              <span className="lie-totals__label">Total billed</span>
              <span className="lie-totals__value">{formatCurrency(totals.total)}</span>
            </div>
            <div className="lie-totals__fact">
              <span className="lie-totals__label">On paid invoices</span>
              <span className="lie-totals__value lie-totals__value--paid">
                {formatCurrency(totals.paid)}
              </span>
            </div>
            <div className="lie-totals__fact">
              <span className="lie-totals__label">On unpaid invoices</span>
              <span className="lie-totals__value lie-totals__value--unpaid">
                {formatCurrency(totals.unpaid)}
              </span>
            </div>
            <div className="lie-totals__fact">
              <span className="lie-totals__label">Lines / invoices</span>
              <span className="lie-totals__value">
                {totals.lines} / {totals.invoices}
              </span>
            </div>
          </div>
        )}

        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                {head('invoice', 'Invoice')}
                {head('date', 'Date')}
                {head('customer', 'Customer')}
                {head('code', 'Product code')}
                <DataTableHeadCell>Description</DataTableHeadCell>
                <DataTableHeadCell align="right">Qty</DataTableHeadCell>
                <DataTableHeadCell align="right">Unit price</DataTableHeadCell>
                {head('total', 'Line total', 'right')}
                <DataTableHeadCell>Paid</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {loading && !result && (
                <DataTableEmpty colSpan={COLUMN_COUNT}>Searching…</DataTableEmpty>
              )}
              {!loading && !result && (
                <DataTableEmpty colSpan={COLUMN_COUNT}>
                  Pick a customer and/or enter keywords, then Search.
                </DataTableEmpty>
              )}
              {result && rows.length === 0 && (
                <DataTableEmpty colSpan={COLUMN_COUNT}>No matching lines.</DataTableEmpty>
              )}
              {pageRows.map(renderRow)}
            </tbody>
          </DataTable>
          {rows.length > 0 && (
            <DataTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              count={rows.length}
              rowsPerPageOptions={[50, 100, 250]}
              onPageChange={setPage}
              onRowsPerPageChange={(n) => {
                setRowsPerPage(n);
                setPage(0);
              }}
            />
          )}
        </DataTableCard>
      </div>
    </div>
  );
};

export default LineItemExplorerPage;
